import React, { useEffect, useMemo, useState } from "react";
import Confetti from "react-confetti";
import DialogueBox from "./DialogueBox";
import StarQuestRoadmap from "./StarQuestRoadmap";

const PROPHECY_BY_CHAPTER = {
  2: "Music is the light that guides the lost.",
  3: "Even dragons tremble at a true song.",
  4: "Courage makes melodies stronger.",
  5: "Joy turns notes into unbreakable bonds.",
  6: "Harmony awakens hidden power.",
  7: "Every nation rises through its anthem.",
  8: "Wisdom lies in listening as much as playing.",
  9: "Only Für Elise can put Doron to sleep.",
};

// Ensure paths resolve from /public even if someone passes "images/foo.png"
const ensurePublic = (p) => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return p.startsWith("/") ? p : `/${p}`;
};

export default function LetterRevealModal({
  open,
  onClose,
  onNext,
  nextButtonText = "Next Chapter",
  chapter,
  mode = "chapter",
  companions = {
    left:  { name: "Pan", avatar: "/images/pan-the-panda.png" },
    right: { name: "Wis", avatar: "/images/wis.png" },
  },
  roadmapStops,
  currentStopIndex,
}) {
  const [burst, setBurst] = useState(false);

  const [w, h] = [
    typeof window !== "undefined" ? window.innerWidth : 800,
    typeof window !== "undefined" ? window.innerHeight : 600,
  ];

  // ✅ Resolve avatars from /public and keep a fallback for Pan
  const PAN_AVATAR = ensurePublic("/images/pan-the-panda.png");
  const WIS_AVATAR = ensurePublic("/images/wis.png");

  const leftCompanion = {
    name: companions?.left?.name ?? "Pan",
    avatar: ensurePublic(companions?.left?.avatar ?? PAN_AVATAR),
  };
  const rightCompanion = {
    name: companions?.right?.name ?? "Wis",
    avatar: ensurePublic(companions?.right?.avatar ?? WIS_AVATAR),
  };

  const wisdom = useMemo(
    () => (chapter ? PROPHECY_BY_CHAPTER[chapter] : null),
    [chapter]
  );

  useEffect(() => {
    if (!open) return;
    setBurst(true);
    const t = setTimeout(() => setBurst(false), 1200);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const isIntro = mode === "intro";
  const title = isIntro ? "Star Quest Begins!" : "Star Quest";

  // Default roadmap (Ch.2–9) — your titles preserved
  const defaultStops = [
    { title: "Zeb",             image: "/images/ChapterTwo/zebra-birthday.png" },
    { title: "Peng",            image: "/images/ChapterThree/Ch3-MelodyPage.png" },
    { title: "Treb Lake",       image: "/images/ChapterFour/Ch4 Slide 2.png" },
    { title: "Rudolph's Problem", image: "/images/ChapterFive/Melody Image.png" },
    { title: "Spider Dance",    image: "/images/ChapterSix/Chp 6 Melody Img .png" },
    { title: "Bamboo Forest",   image: "/images/ChapterSeven/Ch7 Slide 2.png" },
    { title: "Peacock Parade",  image: "/images/ChapterEight/Ch8 Melody.png" },
    { title: "Crocs Boat",      image: "/images/ChapterNine/Ch9 Melody.png" },
  ];

  const stops = roadmapStops ?? defaultStops;
  const highlightIndex =
    typeof currentStopIndex === "number" ? currentStopIndex : 0;

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/55 backdrop-blur-[1px] flex items-center justify-center p-2 sm:p-4"
      onClick={() => onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {burst && (
        <Confetti width={w} height={h} numberOfPieces={140} recycle={false} />
      )}

      {/* Modal shell — ~90% viewport, capped by a sensible max */}
      <div
        className="relative bg-gradient-to-b from-amber-50 to-amber-100 rounded-3xl shadow-2xl border border-amber-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1200px, 90vw)",
          height: "min(900px, 90vh)",
        }}
      >
        {/* Close */}
        <button
          onClick={() => onClose?.()}
          className="absolute right-3 top-2 text-amber-900/80 hover:text-amber-900 text-3xl leading-none"
          aria-label="Close"
        >
          ×
        </button>

        {/* Header */}
        <div className="px-6 md:px-8 py-4 md:py-5 bg-amber-200/70 border-b border-amber-300">
          <div className="text-2xl md:text-3xl font-bold text-amber-900 tracking-wide">
            {title}
          </div>
          {chapter ? (
            <div className="text-amber-800/90 text-sm md:text-base mt-0.5">
              Chapter {chapter}
            </div>
          ) : null}
        </div>

        {/* Body — grow to fill, scroll internally */}
        <div className="flex-1 overflow-y-auto px-5 md:px-8 py-5 md:py-6 space-y-5 md:space-y-6">
          {/* Dialogue area */}
          <div className="rounded-2xl bg-white border border-amber-200 shadow-inner p-4 md:p-6 space-y-3 md:space-y-4">
            {isIntro ? (
              <>
                {/* Pass both avatarSrc and avatar for maximum compatibility */}
                <DialogueBox
                  speaker={leftCompanion.name}
                  avatarSrc={ensurePublic(leftCompanion.avatar) || PAN_AVATAR}
                  avatar={ensurePublic(leftCompanion.avatar) || PAN_AVATAR}
                  tone="info"
                >
                  Doron may be asleep… but we must find a way to defeat him
                  forever and save Treble Town!
                </DialogueBox>
                <DialogueBox
                  speaker={rightCompanion.name}
                  avatarSrc={ensurePublic(rightCompanion.avatar) || WIS_AVATAR}
                  avatar={ensurePublic(rightCompanion.avatar) || WIS_AVATAR}
                  tone="success"
                >
                  Long ago, the Fathers of Four hid a secret weapon somewhere in
                  Treble Town. Only those who follow the path of music can find
                  it. Travel through Treble Town and help the Treblins with your
                  magical melodies. Each Treblin will reward you with a shining
                  star. When all the stars are gathered, the way to defeat Doron
                  will be revealed.
                </DialogueBox>
                <DialogueBox
                  speaker={rightCompanion.name}
                  avatarSrc={ensurePublic(rightCompanion.avatar) || WIS_AVATAR}
                  avatar={ensurePublic(rightCompanion.avatar) || WIS_AVATAR}
                  tone="warning"
                >
                  Best of luck… and remember, music always flows, if you let it.
                </DialogueBox>
              </>
            ) : (
              <>
                <DialogueBox
                  speaker={leftCompanion.name}
                  avatarSrc={ensurePublic(leftCompanion.avatar) || PAN_AVATAR}
                  avatar={ensurePublic(leftCompanion.avatar) || PAN_AVATAR}
                  tone="success"
                >
                  Another star shines bright! Keep helping the Treblins—every
                  star carries wisdom and power.
                </DialogueBox>
                <DialogueBox
                  speaker={rightCompanion.name}
                  avatarSrc={ensurePublic(rightCompanion.avatar) || WIS_AVATAR}
                  avatar={ensurePublic(rightCompanion.avatar) || WIS_AVATAR}
                  tone="info"
                >
                  {wisdom ? (
                    <>
                      Today’s engraving reads: <b>“{wisdom}”</b>
                    </>
                  ) : (
                    <>
                      Place your stars upon the <b>Ancient Sheet</b>. When all
                      are aligned, Giz will discover the melody.
                    </>
                  )}
                </DialogueBox>
              </>
            )}
          </div>

          {/* ⭐ Roadmap / roadway */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 md:p-5">
            <div className="text-amber-900 font-semibold mb-3 md:mb-4 text-base md:text-lg">
              {isIntro
                ? "Your journey through Treble Town:"
                : "Next stops on your journey:"}
            </div>

            {/* Give the roadmap a little more horizontal space */}
            <div className="-mx-1 sm:-mx-2">
              <StarQuestRoadmap stops={stops} currentIndex={highlightIndex} />
            </div>
          </div>
        </div>

        {/* Footer — fixed height */}
        <div className="px-5 md:px-8 py-4 md:py-5 border-t border-amber-200 bg-gradient-to-t from-amber-100 to-amber-100/70 flex flex-col md:flex-row gap-3 md:gap-4 justify-end">
          <button
            onClick={() => onClose?.()}
            className="px-5 py-3 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 active:scale-[.99] transition text-base md:text-lg"
          >
            Got it
          </button>
          {onNext && (
            <button
              onClick={() => {
                onClose?.();
                onNext?.();
              }}
              className="px-6 md:px-7 py-3 md:py-3.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow active:scale-[.99] transition text-base md:text-lg"
            >
              {nextButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
